import * as React from 'react';
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';
import { cn } from '../../lib/cn';

/**
 * ScrollArea — the generic shadcn scroll container on
 * `@radix-ui/react-scroll-area` (keyboard + a11y for free), themed onto the
 * HofSync brand tokens. Near-free to expose: the Radix dep already ships
 * (consumed transitively by `MessageScroller`). This is the standalone layout
 * primitive; `MessageScroller` remains the streaming-chat-specific variant that
 * adds pin-to-bottom autoscroll on top of the same Radix base.
 *
 * All utilities are `tw:`-prefixed and the thumb paints on the brand
 * `--border-strong` token (preflight is excluded — see CLAUDE.md "Scoping").
 */
export type ScrollAreaProps = React.ComponentPropsWithoutRef<
  typeof ScrollAreaPrimitive.Root
>;
export type ScrollBarProps = React.ComponentPropsWithoutRef<
  typeof ScrollAreaPrimitive.Scrollbar
>;

const ScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Scrollbar>,
  ScrollBarProps
>(({ className, orientation = 'vertical', ...props }, ref) => (
  <ScrollAreaPrimitive.Scrollbar
    ref={ref}
    orientation={orientation}
    className={cn(
      'tw:flex tw:touch-none tw:select-none tw:transition-colors',
      orientation === 'vertical' &&
        'tw:h-full tw:w-2.5 tw:border-l tw:border-l-transparent tw:p-0.5',
      orientation === 'horizontal' &&
        'tw:h-2.5 tw:flex-col tw:border-t tw:border-t-transparent tw:p-0.5',
      className
    )}
    {...props}
  >
    <ScrollAreaPrimitive.Thumb className="tw:relative tw:flex-1 tw:rounded-full tw:bg-[var(--border-strong)]" />
  </ScrollAreaPrimitive.Scrollbar>
));
ScrollBar.displayName = ScrollAreaPrimitive.Scrollbar.displayName;

const ScrollArea = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  ScrollAreaProps
>(({ className, children, ...props }, ref) => (
  <ScrollAreaPrimitive.Root
    ref={ref}
    className={cn('tw:relative tw:overflow-hidden', className)}
    {...props}
  >
    <ScrollAreaPrimitive.Viewport className="tw:h-full tw:w-full tw:rounded-[inherit]">
      {children}
    </ScrollAreaPrimitive.Viewport>
    <ScrollBar />
    <ScrollAreaPrimitive.Corner />
  </ScrollAreaPrimitive.Root>
));
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;

export { ScrollArea, ScrollBar };
